<template>
  <div>
    <!-- 컨텍스트: 매장 선택 -->
    <el-form :inline="true" :size="size" @submit.native.prevent>
      <el-form-item v-if="!isStoreMng" :label="$t('매장 코드')">
        <el-input
          v-model="storeCdInput"
          :placeholder="$t('그룹 매장 코드')"
          style="width: 140px"
          @keyup.enter.native="loadContext" />
      </el-form-item>
      <el-form-item v-if="!isStoreMng">
        <el-button icon="el-icon-search" :size="size" @click="loadContext">{{ $t('조회') }}</el-button>
      </el-form-item>
      <el-form-item v-if="group.length">
        <el-tag
          v-for="m in group"
          :key="m.storeCd"
          :type="m.isPrimary ? 'danger' : ''"
          size="small"
          style="margin-right: 6px">
          {{ m.storeNm }} (#{{ m.storeCd }})<template v-if="m.isPrimary"> ★{{ $t('대표') }}</template>
        </el-tag>
      </el-form-item>
      <el-form-item v-if="storeCd">
        <el-button v-if="canEdit" icon="el-icon-plus" :size="size" @click="openBoardDialog()">{{
          $t('메뉴판 추가')
        }}</el-button>
        <el-button v-if="canEdit" icon="el-icon-copy-document" :size="size" @click="openMergeWizard">{{
          $t('중복 메뉴 통합')
        }}</el-button>
        <el-button v-if="canEdit" icon="el-icon-refresh" :size="size" @click="materializeGroup">{{
          $t('재실현(동기화)')
        }}</el-button>
      </el-form-item>
    </el-form>

    <el-alert
      v-if="deliveryConflicts.length"
      :title="
        $t('배민 샵인샵은 가게 간 동일 메뉴 판매가 불가합니다. 다음 메뉴가 복수 매장에서 배달로 판매됩니다: ') +
        deliveryConflicts.join(', ')
      "
      type="warning"
      show-icon
      style="margin-bottom: 10px" />

    <!-- 장착 매트릭스: 행=메뉴판, 열=그룹 매장 -->
    <el-table :data="boards" border :size="size" v-loading="loading" :height="tableHeight">
      <el-table-column :label="$t('메뉴판')" min-width="180" fixed>
        <template slot-scope="scope">
          <div>
            <b>{{ scope.row.boardNm }}</b>
            <el-tag v-if="scope.row.status === 'DRAFT'" size="mini" type="info">DRAFT</el-tag>
            <!-- 앱 종류는 판의 속성이라 뱃지가 판 이름 옆에 붙는다 (장착 셀이 아니다) -->
            <el-tag size="mini" type="success" style="margin-left: 4px">{{ appKindsLabel(scope.row.appKinds) }}</el-tag>
            <span class="board-ord">{{ $t('순서') }} {{ scope.row.ord }}</span>
          </div>
          <div class="board-items-preview">
            {{ itemsPreview(scope.row) }}
          </div>
          <div v-if="canEdit">
            <el-button size="mini" type="text" @click="openBoardDialog(scope.row)">{{ $t('편집') }}</el-button>
            <el-button size="mini" type="text" style="color: #f56c6c" @click="removeBoard(scope.row)">{{
              $t('삭제')
            }}</el-button>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        v-for="member in group"
        :key="member.storeCd"
        :label="`${member.storeNm}${member.isPrimary ? ' ★' : ''}`"
        min-width="170"
        align="center">
        <template slot-scope="scope">
          <!-- 셀이 말하는 것은 장착 여부 하나다 — 앱 종류는 판 속성으로 옮겨졌다 -->
          <div>
            <el-tag v-if="isAttached(scope.row, member.storeCd)" size="mini" type="success">{{ $t('장착') }}</el-tag>
            <span v-else class="not-attached">-</span>
          </div>
          <el-button v-if="canEdit" size="mini" type="text" @click="toggleAttach(scope.row, member)">{{
            isAttached(scope.row, member.storeCd) ? $t('해제') : $t('장착')
          }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 판 편집 다이얼로그 -->
    <el-dialog
      :title="boardForm.menuBoardCd ? $t('메뉴판 편집') : $t('메뉴판 추가')"
      :visible.sync="boardDialogVisible"
      width="860px"
      :close-on-click-modal="false">
      <el-form :size="size" label-width="110px" @submit.native.prevent>
        <el-form-item :label="$t('메뉴판 이름')">
          <el-input v-model="boardForm.boardNm" maxlength="50" style="width: 320px" />
        </el-form-item>
        <el-form-item :label="$t('설명')">
          <el-input v-model="boardForm.boardDesc" maxlength="250" style="width: 480px" />
        </el-form-item>
        <el-form-item :label="$t('노출 앱')">
          <el-checkbox-group v-model="boardForm.appKinds">
            <el-checkbox label="qr">{{ $t('오더톡') }}</el-checkbox>
            <el-checkbox label="kiosk">{{ $t('키오스크') }}</el-checkbox>
            <el-checkbox label="pos">{{ $t('POS') }}</el-checkbox>
          </el-checkbox-group>
          <div class="hint">
            {{
              $t('* 고른 앱에서만 이 판의 메뉴가 보입니다. 그 앱에 걸린 판이 하나도 없는 매장은 메뉴 전체가 보입니다.')
            }}
          </div>
        </el-form-item>
        <el-form-item :label="$t('순서')">
          <el-input-number
            v-model="boardForm.ord"
            size="mini"
            :min="0"
            :max="9999"
            controls-position="right"
            style="width: 100px" />
          <!-- ord 가 노출을 정한다는 오해가 이 화면의 유일한 함정이다 (07 §8) -->
          <div class="hint">
            {{
              $t(
                '* 같은 앱에 걸린 판이 여럿이면 순서가 빠른 판의 분류·정렬을 씁니다. 메뉴는 한 판에만 있어도 보입니다.'
              )
            }}
          </div>
        </el-form-item>
        <el-form-item :label="$t('구성 메뉴')">
          <el-button size="mini" icon="el-icon-plus" @click="openMenuPicker">{{ $t('기존 메뉴 담기') }}</el-button>
          <el-table :data="boardForm.items" size="mini" border style="margin-top: 8px">
            <el-table-column prop="smenuNm" :label="$t('메뉴명')" min-width="140" />
            <el-table-column :label="$t('가격')" width="90" align="right">
              <template slot-scope="scope">{{ formatMoney(scope.row.price) }}</template>
            </el-table-column>
            <el-table-column :label="$t('원본 매장')" width="110">
              <template slot-scope="scope">#{{ scope.row.ownerStoreCd }}</template>
            </el-table-column>
            <el-table-column :label="$t('섹션(카테고리)')" width="160">
              <template slot-scope="scope">
                <el-input v-model="scope.row.sectionNm" size="mini" maxlength="50" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('순서')" width="80">
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.ord"
                  size="mini"
                  :min="1"
                  :max="9999"
                  controls-position="right"
                  style="width: 70px" />
              </template>
            </el-table-column>
            <el-table-column width="60">
              <template slot-scope="scope">
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-delete"
                  style="color: #f56c6c"
                  @click="boardForm.items.splice(scope.$index, 1)" />
              </template>
            </el-table-column>
          </el-table>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button icon="el-icon-check" :size="size" @click="saveBoard">{{ $t('저장') }}</el-button>
        <el-button icon="el-icon-close" :size="size" @click="boardDialogVisible = false">{{ $t('닫기') }}</el-button>
      </div>
    </el-dialog>

    <!-- 기존 메뉴 담기 다이얼로그 -->
    <el-dialog :title="$t('기존 메뉴 담기')" :visible.sync="menuPickerVisible" width="720px" append-to-body>
      <el-form :inline="true" size="mini" @submit.native.prevent>
        <el-form-item :label="$t('메뉴명')">
          <el-input v-model="menuPickerFilter" style="width: 180px" />
        </el-form-item>
        <el-form-item :label="$t('섹션(카테고리)')">
          <el-input v-model="menuPickerSection" :placeholder="$t('담을 섹션명')" style="width: 160px" />
        </el-form-item>
      </el-form>
      <el-table
        :data="filteredGroupMenus"
        size="mini"
        border
        height="360"
        ref="menuPickerTable"
        @selection-change="(rows) => (menuPickerSelection = rows)">
        <el-table-column type="selection" width="42" />
        <el-table-column prop="smenuNm" :label="$t('메뉴명')" min-width="140" />
        <el-table-column :label="$t('가격')" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.price) }}</template>
        </el-table-column>
        <el-table-column prop="storeNm" :label="$t('매장')" width="140" />
        <el-table-column :label="$t('카테고리')" min-width="120">
          <template slot-scope="scope">{{ (scope.row.categoryNms || []).join(', ') }}</template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button icon="el-icon-check" size="small" @click="addPickedMenus">{{ $t('담기') }}</el-button>
        <el-button icon="el-icon-close" size="small" @click="menuPickerVisible = false">{{ $t('닫기') }}</el-button>
      </div>
    </el-dialog>

    <!-- 중복 통합 마법사 -->
    <el-dialog :title="$t('중복 메뉴 통합')" :visible.sync="mergeWizardVisible" width="760px">
      <p class="hint">
        {{
          $t(
            '그룹 매장들에 중복 등록된 메뉴(예: 브랜드별 콜라)를 원본-사본으로 통합합니다. 메뉴 코드가 유지되어 매출 이력이 안전합니다.'
          )
        }}
      </p>
      <div v-if="!mergeCandidates.length" style="padding: 16px; color: #909399">
        {{ $t('통합 후보가 없습니다.') }}
      </div>
      <div v-for="cand in mergeCandidates" :key="cand.normalizedNm" class="merge-group">
        <el-table :data="cand.menus" size="mini" border>
          <el-table-column :label="$t('원본 선택')" width="90" align="center">
            <template slot-scope="scope">
              <el-radio v-model="mergeMasterSelection[cand.normalizedNm]" :label="scope.row.smenuCd" @change="() => {}"
                ><span></span
              ></el-radio>
            </template>
          </el-table-column>
          <el-table-column prop="smenuNm" :label="$t('메뉴명')" min-width="120" />
          <el-table-column :label="$t('가격')" width="90" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.price) }}</template>
          </el-table-column>
          <el-table-column prop="storeNm" :label="$t('매장')" width="140" />
          <el-table-column prop="barcode" :label="$t('바코드')" width="110" />
        </el-table>
        <el-button size="mini" type="primary" plain style="margin: 6px 0 14px" @click="doMerge(cand)">{{
          $t('이 그룹 통합')
        }}</el-button>
      </div>
      <div slot="footer">
        <el-button icon="el-icon-close" size="small" @click="mergeWizardVisible = false">{{ $t('닫기') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { initData } from '@/utils/constants'
import { parseStoreCd } from '@/utils/posDeliverySoldout'
import { MENU_BOARD_WRITE_ACTION } from '@/utils/deliveryMenuSync'

/**
 * 판을 진열할 앱 — 서버 `AppKind` enum 과 같은 어휘다(소문자). 서버가 `'ALL'` 로 내려주면
 * 전부 고른 것이고, 저장할 때 전부 고르면 서버가 다시 `'ALL'` 로 접는다.
 * 계약: ordern-workspace/docs/shop-in-shop/07-menu-board-programming.md §3.1
 */
const APP_KINDS = ['qr', 'kiosk', 'pos']
const APP_KINDS_ALL = 'ALL'

export default {
  mixins: [mainVueMixin],

  data() {
    const data = initData()
    data.url = '/api/menu-board'
    data.storeCdInput = ''
    data.storeCd = null
    data.group = []
    data.boards = []
    data.loading = false
    data.deliveryConflicts = []

    data.boardDialogVisible = false
    data.boardForm = {
      menuBoardCd: null,
      ownerStoreCd: null,
      boardNm: '',
      boardDesc: '',
      appKinds: [...APP_KINDS],
      ord: 0,
      items: [],
    }

    data.menuPickerVisible = false
    data.menuPickerFilter = ''
    data.menuPickerSection = ''
    data.menuPickerSelection = []
    data.groupMenus = []

    data.mergeWizardVisible = false
    data.mergeCandidates = []
    data.mergeMasterSelection = {}

    return data
  },

  computed: {
    isStoreMng() {
      return this.$store.getters['auth/authId'] === 'storeMng'
    },
    canEdit() {
      return this.checkAuthActionAccess(MENU_BOARD_WRITE_ACTION)
    },
    filteredGroupMenus() {
      const filter = (this.menuPickerFilter || '').trim()
      const already = new Set(this.boardForm.items.map((i) => i.smenuCd))
      return this.groupMenus
        .filter((m) => !already.has(m.smenuCd))
        .filter((m) => !filter || (m.smenuNm || '').includes(filter))
    },
  },

  created() {
    // 쿼리 매장(`?storeCd=`)이 먼저다 — 관리점·운영사(R-E2)는 세션 매장이 없다(`/store/getStore` 가 빈 200).
    // 범위는 서버가 본다(`/api/menu-board/**` 가 매장마다 검사한다)
    const routed = parseStoreCd(this.$route.query.storeCd)
    if (routed != null) {
      this.storeCdInput = String(routed)
      this.storeCd = routed
      this.loadMatrix()
      return
    }
    if (this.isStoreMng) {
      this.resolveOwnStore()
    }
  },

  methods: {
    formatMoney(v) {
      return (v ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    appKindLabel(kind) {
      return { qr: this.$t('오더톡'), kiosk: this.$t('키오스크'), pos: this.$t('POS') }[kind] || kind
    },
    /** 서버의 `app_kinds` 문자열('ALL' 또는 콤마 목록)을 체크박스 배열로 — 파싱 규칙은 서버와 같다 */
    parseAppKinds(raw) {
      const text = (raw || '').trim()
      if (!text || text.toUpperCase() === APP_KINDS_ALL) return [...APP_KINDS]
      const picked = APP_KINDS.filter((kind) => text.split(',').some((token) => token.trim() === kind))
      // 하나도 못 읽었으면 전체로 본다 — 좁히는 쪽으로 틀리면 사장님 화면에서 메뉴가 사라진다
      return picked.length ? picked : [...APP_KINDS]
    },
    appKindsLabel(raw) {
      const kinds = this.parseAppKinds(raw)
      if (kinds.length === APP_KINDS.length) return this.$t('전체')
      return kinds.map((k) => this.appKindLabel(k)).join('·')
    },
    itemsPreview(board) {
      const names = (board.items || []).map((i) => i.smenuNm).filter(Boolean)
      const head = names.slice(0, 4).join(', ')
      return names.length > 4 ? `${head} 외 ${names.length - 4}` : head || this.$t('(비어있음)')
    },
    isAttached(board, storeCd) {
      const attach = (board.attaches || []).find((a) => a.storeCd === storeCd)
      return !!(attach && attach.attached)
    },

    async resolveOwnStore() {
      try {
        const res = await this.$axios.get('/store/getStore')
        if (res.data && res.data.storeCd) {
          this.storeCd = res.data.storeCd
          await this.loadMatrix()
        }
      } catch (e) {
        this.$message.error(this.$t('매장 정보를 불러오지 못했습니다.'))
      }
    },
    async loadContext() {
      const cd = parseInt(this.storeCdInput, 10)
      if (!cd) {
        this.$message.warning(this.$t('매장 코드를 입력하십시오.'))
        return
      }
      this.storeCd = cd
      await this.loadMatrix()
    },
    async loadMatrix() {
      if (!this.storeCd) return
      this.loading = true
      try {
        const res = await this.$axios.get(`/api/menu-board/matrix/${this.storeCd}`)
        this.group = res.data.group || []
        this.boards = res.data.boards || []
      } catch (e) {
        this.group = []
        this.boards = []
        this.$message.error(this.serverError(e, this.$t('메뉴판 조회에 실패했습니다.')))
      } finally {
        this.loading = false
      }
    },

    // ---------------- 판 편집 ----------------
    openBoardDialog(board) {
      if (board) {
        this.boardForm = {
          menuBoardCd: board.menuBoardCd,
          ownerStoreCd: board.ownerStoreCd,
          boardNm: board.boardNm,
          boardDesc: board.boardDesc,
          appKinds: this.parseAppKinds(board.appKinds),
          ord: board.ord || 0,
          items: (board.items || []).map((i) => ({ ...i })),
        }
      } else {
        const primary = this.group.find((m) => m.isPrimary)
        this.boardForm = {
          menuBoardCd: null,
          ownerStoreCd: primary ? primary.storeCd : this.storeCd,
          boardNm: '',
          boardDesc: '',
          appKinds: [...APP_KINDS],
          ord: 0,
          items: [],
        }
      }
      this.boardDialogVisible = true
    },
    async saveBoard() {
      if (!this.boardForm.boardNm) {
        this.$message.warning(this.$t('메뉴판 이름을 입력하십시오.'))
        return
      }
      // 서버도 같은 것을 거절한다(400) — 여기서 먼저 말하는 이유는 사장님이 저장을 누른 뒤가
      // 아니라 고르는 자리에서 알아야 하기 때문이다. 두 곳에 있는 것은 계약이지 중복이 아니다
      if (!this.boardForm.appKinds.length) {
        this.$message.warning(this.$t('메뉴판을 보일 앱을 하나 이상 고르십시오.'))
        return
      }
      const payload = {
        ownerStoreCd: this.boardForm.ownerStoreCd,
        boardNm: this.boardForm.boardNm,
        boardDesc: this.boardForm.boardDesc,
        appKinds: this.boardForm.appKinds,
        ord: this.boardForm.ord || 0,
        items: this.boardForm.items.map((i, idx) => ({
          smenuCd: i.smenuCd,
          sectionNm: i.sectionNm || this.$t('메뉴'),
          ord: i.ord || idx + 1,
        })),
      }
      try {
        if (this.boardForm.menuBoardCd) {
          await this.$axios.put(`/api/menu-board/${this.boardForm.menuBoardCd}`, payload)
        } else {
          await this.$axios.post('/api/menu-board', payload)
        }
        this.boardDialogVisible = false
        this.$message.success(this.$t('저장되었습니다.'))
        await this.loadMatrix()
      } catch (e) {
        this.$message.error(this.serverError(e, this.$t('저장에 실패했습니다.')))
      }
    },
    removeBoard(board) {
      this.$confirm(
        this.$t(
          '메뉴판을 삭제하면 이 판에서만 노출되던 공유 메뉴가 장착 매장 전체에서 숨김 처리됩니다. 계속하시겠습니까?'
        ),
        this.$t('메뉴판 삭제'),
        { type: 'warning' }
      )
        .then(async () => {
          try {
            await this.$axios.delete(`/api/menu-board/${board.menuBoardCd}`)
            this.$message.success(this.$t('삭제되었습니다.'))
            await this.loadMatrix()
          } catch (e) {
            this.$message.error(this.serverError(e, this.$t('삭제에 실패했습니다.')))
          }
        })
        .catch(() => {})
    },

    // ---------------- 기존 메뉴 담기 ----------------
    async openMenuPicker() {
      try {
        const res = await this.$axios.get(`/api/menu-board/group-menus/${this.storeCd}`)
        this.groupMenus = res.data || []
        this.menuPickerVisible = true
      } catch (e) {
        this.$message.error(this.serverError(e, this.$t('메뉴 조회에 실패했습니다.')))
      }
    },
    addPickedMenus() {
      const section = this.menuPickerSection || this.$t('메뉴')
      this.menuPickerSelection.forEach((m) => {
        this.boardForm.items.push({
          smenuCd: m.smenuCd,
          smenuNm: m.smenuNm,
          price: m.price,
          ownerStoreCd: m.storeCd,
          sectionNm: section,
          ord: this.boardForm.items.length + 1,
        })
      })
      this.menuPickerVisible = false
    },

    // ---------------- 장착 ----------------
    // 채널 축이 사라져 고를 것이 없다 — 셀은 on/off 이므로 다이얼로그 없이 즉시 토글한다.
    // 해제는 사본을 숨기므로(삭제 아님) 되돌리는 비용이 재장착 한 번이다
    toggleAttach(board, member) {
      const attached = !this.isAttached(board, member.storeCd)
      const ask = attached
        ? this.$t('이 매장에 메뉴판을 장착하고 사본을 실현합니다. 계속하시겠습니까?')
        : this.$t('이 매장에서 메뉴판을 해제합니다. 이 판에서만 노출되던 메뉴가 숨김 처리됩니다(삭제 아님).')
      this.$confirm(ask, attached ? this.$t('장착') : this.$t('해제'), { type: attached ? 'info' : 'warning' })
        .then(async () => {
          try {
            const res = await this.$axios.post('/api/menu-board/attach', {
              storeCd: member.storeCd,
              menuBoardCd: board.menuBoardCd,
              attached,
            })
            this.deliveryConflicts = (res.data && res.data.deliveryConflicts) || []
            this.$message.success(
              attached ? this.$t('장착이 저장되고 사본이 실현되었습니다.') : this.$t('해제되었습니다.')
            )
            await this.loadMatrix()
          } catch (e) {
            this.$message.error(this.serverError(e, this.$t('장착 저장에 실패했습니다.')))
          }
        })
        .catch(() => {})
    },

    // ---------------- 재실현 ----------------
    materializeGroup() {
      this.$confirm(
        this.$t(
          '그룹 전체 매장의 사본을 선언 상태로 재동기화합니다(멱등 — 가격/품절 오버라이드는 보존). 계속하시겠습니까?'
        ),
        this.$t('재실현'),
        { type: 'info' }
      )
        .then(async () => {
          try {
            for (const m of this.group) {
              await this.$axios.post(`/api/menu-board/materialize/${m.storeCd}`)
            }
            this.$message.success(this.$t('재실현이 완료되었습니다.'))
            await this.loadMatrix()
          } catch (e) {
            this.$message.error(this.serverError(e, this.$t('재실현에 실패했습니다.')))
          }
        })
        .catch(() => {})
    },

    // ---------------- 중복 통합 ----------------
    async openMergeWizard() {
      try {
        const res = await this.$axios.get(`/api/menu-board/merge-candidates/${this.storeCd}`)
        this.mergeCandidates = res.data || []
        // Vue 2 반응성 — 키를 선초기화해야 라디오 선택이 화면에 반영된다
        this.mergeMasterSelection = Object.fromEntries(this.mergeCandidates.map((c) => [c.normalizedNm, null]))
        this.mergeWizardVisible = true
      } catch (e) {
        this.$message.error(this.serverError(e, this.$t('통합 후보 조회에 실패했습니다.')))
      }
    },
    async doMerge(cand) {
      const masterCd = this.mergeMasterSelection[cand.normalizedNm]
      if (!masterCd) {
        this.$message.warning(this.$t('원본으로 사용할 메뉴를 선택하십시오.'))
        return
      }
      const copyCds = cand.menus.map((m) => m.smenuCd).filter((cd) => cd !== masterCd)
      this.$confirm(
        this.$t(
          '선택한 원본으로 나머지 메뉴를 통합합니다. 통합 후 이름/이미지 등 정체성이 원본 기준으로 동기화되며, 가격 차이는 매장별 오버라이드로 보존됩니다.'
        ),
        this.$t('중복 통합'),
        { type: 'warning' }
      )
        .then(async () => {
          try {
            await this.$axios.post('/api/menu-board/merge', {
              masterSmenuCd: masterCd,
              copySmenuCds: copyCds,
              // 요청 고유 요소(타임스탬프) 포함 — 재시도 이중 실행만 차단하고 의도적 재병합(detach 후)은 허용
              idempotencyKey: `merge-${masterCd}-${copyCds.join('_')}-${Date.now()}`,
            })
            this.$message.success(this.$t('통합되었습니다.'))
            await this.openMergeWizard()
            await this.loadMatrix()
          } catch (e) {
            this.$message.error(this.serverError(e, this.$t('통합에 실패했습니다.')))
          }
        })
        .catch(() => {})
    },

    serverError(e, fallback) {
      return (e.response && e.response.data && e.response.data.message) || fallback
    },
  },
}
</script>

<style scoped>
.board-items-preview {
  font-size: 12px;
  color: #909399;
  margin: 2px 0;
  white-space: normal;
}
.not-attached {
  color: #c0c4cc;
}
.board-ord {
  font-size: 11px;
  color: #909399;
  margin-left: 6px;
}
.hint {
  color: #909399;
  font-size: 12px;
  margin: 8px 0;
}
.merge-group {
  margin-bottom: 8px;
}
</style>
